import React, { useState, useEffect } from 'react';
import './ConfirmationDialog.module.css';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDangerous?: boolean;
  requireDoubleConfirm?: boolean;
  doubleConfirmMessage?: string;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isDangerous = false,
  requireDoubleConfirm = false,
  doubleConfirmMessage = 'Are you absolutely sure? This action cannot be undone.'
}) => {
  const [showSecondConfirm, setShowSecondConfirm] = useState(false);

  // Reset second confirmation when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowSecondConfirm(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFirstConfirm = () => {
    if (requireDoubleConfirm && !showSecondConfirm) {
      setShowSecondConfirm(true);
    } else {
      onConfirm();
      setShowSecondConfirm(false);
    }
  };

  const handleCancel = () => {
    setShowSecondConfirm(false);
    onCancel();
  };

  const currentMessage = showSecondConfirm ? doubleConfirmMessage : message;
  const currentConfirmText = showSecondConfirm ? 'Yes, Delete Permanently' : confirmText;

  return (
    <div className="confirmation-overlay" onClick={handleCancel}>
      <div
        className={`confirmation-dialog ${showSecondConfirm ? 'second-confirm' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirmation-header">
          <h2>{title}</h2>
          {showSecondConfirm && (
            <div className="second-confirm-badge">Final Warning</div>
          )}
        </div>
        <div className="confirmation-body">
          {showSecondConfirm && (
            <div className="warning-banner">
              <svg
                className="warning-icon"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>This is a destructive action</span>
            </div>
          )}
          <p>{currentMessage}</p>
        </div>
        <div className="confirmation-footer">
          <button onClick={handleCancel} className="btn-cancel">
            {cancelText}
          </button>
          <button
            onClick={handleFirstConfirm}
            className={isDangerous ? 'btn-danger' : 'btn-confirm'}
          >
            {currentConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
